import * as React from 'react';

/**
 * Touch selection for Radix menu items, without relying on iOS's synthetic click.
 *
 * Radix menu items select on `click`. On iOS, a tap's click is synthesised by
 * WebKit after touchend, and on the iPhone Duo it is intermittently never
 * sent. The tap debugger logged failed taps as `pointerdown touchstart
 * pointerup touchend`: no mouse events and no click, nothing cancelled by the
 * app, and the item had cursor:pointer. So menu items needed 2-3 taps.
 *
 * For touch and pen, this selects on pointerup instead, by dispatching the
 * click ourselves when the finger lifts on the item without having moved. If
 * WebKit's own (trusted) click follows anyway, it is swallowed, so an item
 * never fires twice (that would matter for checkbox items, which stay open).
 * Mouse input is untouched.
 */
type Handlers = {
  onPointerDown?: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp?: React.PointerEventHandler<HTMLDivElement>;
  onClickCapture?: React.MouseEventHandler<HTMLDivElement>;
};

const MOVE_TOLERANCE = 10; // px: more than this is a scroll, not a tap
const DUPLICATE_WINDOW = 800; // ms: a trusted click this soon after is WebKit's late copy

export function useTouchMenuSelect(props: Handlers): Required<Handlers> {
  const down = React.useRef<{ x: number; y: number; id: number } | null>(null);
  const selectedAt = React.useRef(0);

  return {
    onPointerDown: (e) => {
      props.onPointerDown?.(e);
      down.current =
        e.pointerType === 'mouse' ? null : { x: e.clientX, y: e.clientY, id: e.pointerId };
    },
    onPointerUp: (e) => {
      props.onPointerUp?.(e);
      const start = down.current;
      down.current = null;
      if (!start || e.pointerId !== start.id || e.defaultPrevented) return;
      if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > MOVE_TOLERANCE) return;
      if (e.currentTarget.hasAttribute('data-disabled')) return;
      selectedAt.current = performance.now();
      e.currentTarget.click();
      // Radix's own pointerup handler (composed after ours) clicks when it
      // thinks the press started elsewhere; selection just reset that flag,
      // so stop it from firing a second click.
      e.preventDefault();
    },
    onClickCapture: (e) => {
      if (e.nativeEvent.isTrusted && performance.now() - selectedAt.current < DUPLICATE_WINDOW) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      props.onClickCapture?.(e);
    },
  };
}
